import type { ReactNode } from 'react';
import { forwardRef } from 'react';
import type { FormProps } from 'react-router';
import { Form, useLocation } from 'react-router';

import { useBloomBasename } from '@jane/bloom/hooks/useBloomBasename';
import { withoutInitialSlashes } from '@jane/bloom/utils/strings/withoutInitialSlashes';

/**
 * Renders a Remix Form setting action relative to Bloom Basename.
 * @example
 * <FormWithBasename action="/login" name="login-form">
 * </FormWithBasename>
 * @param {string} name - The name of the form.
 * @param {string} action - The path to the action. If omitted the current path is used.
 * @param {string} intent - The intent of the form action. Can be used for multiple actions on the same path.
 */
export const FormWithBasename = forwardRef<
  HTMLFormElement,
  {
    children: ReactNode;
    intent?: string;
    name: string;
  } & Omit<FormProps, 'name'>
>(({ action, children, intent, name, ...props }, ref) => {
  const basename = useBloomBasename();
  const path = useLocation();

  let resolvedAction;

  if (action) {
    resolvedAction = `${basename}${withoutInitialSlashes(action)}`;
  }

  if (!resolvedAction) {
    resolvedAction = path.pathname + path.search + path.hash;
  }

  return (
    <Form ref={ref} action={resolvedAction} name={name} {...props}>
      {intent && <input type="hidden" name="intent" value={intent} />}
      {children}
    </Form>
  );
});

FormWithBasename.displayName = 'FormWithBasename';
